export type SearchPrimitive = string | number | boolean;
export type SearchParamValue = SearchPrimitive | SearchPrimitive[] | undefined;
export type SearchParamsInput = Record<string, SearchParamValue>;
export type SearchParamsSource = Record<string, unknown>;

function normalizePrimitive(value: SearchPrimitive): string {
	return String(value);
}

function isSearchPrimitive(value: unknown): value is SearchPrimitive {
	return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function normalizeUnknownSearchValue(value: unknown): string | string[] | undefined {
	if (Array.isArray(value)) {
		const normalizedValues = value.filter(isSearchPrimitive).map(normalizePrimitive);
		return normalizedValues.length > 0 ? normalizedValues : undefined;
	}

	return isSearchPrimitive(value) ? normalizePrimitive(value) : undefined;
}

export function getSearchParam(search: SearchParamsSource, key: string): string | undefined {
	const value = search[key];

	if (Array.isArray(value)) {
		const [firstValue] = value;
		return typeof firstValue === "string" || typeof firstValue === "number" || typeof firstValue === "boolean"
			? String(firstValue)
			: undefined;
	}

	return typeof value === "string" || typeof value === "number" || typeof value === "boolean"
		? String(value)
		: undefined;
}

export function getSearchParamValues(search: SearchParamsSource, key: string): string[] {
	const value = search[key];

	if (Array.isArray(value)) {
		return value
			.filter(
				(entry): entry is string | number | boolean =>
					typeof entry === "string" || typeof entry === "number" || typeof entry === "boolean",
			)
			.map((entry) => String(entry));
	}

	if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
		return [String(value)];
	}

	return [];
}

export function toSearchParamsRecord(
	search: SearchParamsInput | SearchParamsSource = {},
): Record<string, string | string[] | undefined> {
	return Object.fromEntries(
		Object.entries(search).flatMap(([key, value]) => {
			const normalizedValue = normalizeUnknownSearchValue(value);
			return normalizedValue === undefined ? [] : [[key, normalizedValue]];
		}),
	);
}

export function buildQueryString(search: SearchParamsInput | SearchParamsSource = {}): string {
	return Object.entries(toSearchParamsRecord(search))
		.flatMap(([key, value]) => {
			if (value === undefined) return [];

			if (Array.isArray(value)) {
				return value.map((entry) => `${encodeURIComponent(key)}=${encodeURIComponent(entry)}`);
			}

			return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
		})
		.join("&");
}
